/**
 * Shared Style Configuration
 * Single source of truth for styles used by both React components and Export
 *
 * IMPORTANT: When updating styles, update ONLY this file.
 * Both React components and exportUtils.ts will use these values.
 */

// =============================================================================
// BREAKPOINTS
// =============================================================================

export const breakpoints = {
  mobile: 768, // < 768px = mobile
  tablet: 1024, // >= 768 && < 1024 = tablet
  desktop: 1024, // >= 1024 = desktop
} as const;

/**
 * Check if width is mobile
 */
export const isMobileWidth = (width: number): boolean => width < breakpoints.mobile;

/**
 * Check if width is tablet
 */
export const isTabletWidth = (width: number): boolean => width >= breakpoints.mobile && width < breakpoints.tablet;

/**
 * Check if width is desktop
 */
export const isDesktopWidth = (width: number): boolean => width >= breakpoints.desktop;

// =============================================================================
// BUTTON STYLES
// =============================================================================

export const buttonSizes = {
  sm: {
    mobile: { px: 12, py: 6, fontSize: 13 },
    desktop: { px: 16, py: 8, fontSize: 14 },
  },
  md: {
    mobile: { px: 20, py: 10, fontSize: 14 },
    desktop: { px: 24, py: 12, fontSize: 16 },
  },
  lg: {
    mobile: { px: 24, py: 12, fontSize: 16 },
    desktop: { px: 32, py: 16, fontSize: 18 },
  },
} as const;

export type ButtonSize = keyof typeof buttonSizes;

export const getButtonSizeStyles = (size: ButtonSize, isMobile: boolean) => {
  const sizeConfig = buttonSizes[size] || buttonSizes.md;
  return isMobile ? sizeConfig.mobile : sizeConfig.desktop;
};

export const buttonVariants = {
  primary: (primaryColor: string) => ({
    backgroundColor: primaryColor,
    color: "#ffffff",
    border: "none",
  }),
  secondary: (secondaryColor: string) => ({
    backgroundColor: secondaryColor,
    color: "#ffffff",
    border: "none",
  }),
  outline: (primaryColor: string) => ({
    backgroundColor: "transparent",
    color: primaryColor,
    border: `2px solid ${primaryColor}`,
  }),
  ghost: (primaryColor: string) => ({
    backgroundColor: "transparent",
    color: primaryColor,
    border: "none",
  }),
} as const;

export type ButtonVariant = keyof typeof buttonVariants;

export const getButtonVariantStyles = (
  variant: ButtonVariant,
  primaryColor: string,
  secondaryColor: string,
  customBgColor?: string,
  customTextColor?: string
): { background: string; color: string; border: string } => {
  // If custom colors provided, use them — border is handled separately by borderColor/borderWidth props
  if (customBgColor || customTextColor) {
    const bg = customBgColor || primaryColor;
    const color = customTextColor || "#ffffff";
    return { background: bg, color, border: "none" };
  }

  // Primary uses gradient
  if (variant === "primary") {
    return {
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
      color: "#ffffff",
      border: "none",
    };
  }

  // Secondary uses solid secondary color
  if (variant === "secondary") {
    return {
      background: secondaryColor,
      color: "#ffffff",
      border: "none",
    };
  }

  // Outline variant
  if (variant === "outline") {
    return {
      background: "transparent",
      color: primaryColor,
      border: `2px solid ${primaryColor}`,
    };
  }

  // Ghost variant
  return {
    background: "transparent",
    color: primaryColor,
    border: "none",
  };
};

// =============================================================================
// TEXT STYLES
// =============================================================================

export const fontWeights = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export type FontWeight = keyof typeof fontWeights;

/**
 * Calculate mobile font size based on desktop size
 * - fontSize > 24: multiply by 0.7
 * - fontSize > 18: multiply by 0.85
 * - otherwise: same size
 */
export const getMobileFontSize = (desktopSize: number): number => {
  if (desktopSize > 24) return Math.round(desktopSize * 0.7);
  if (desktopSize > 18) return Math.round(desktopSize * 0.85);
  return desktopSize;
};

/**
 * Get responsive font size
 */
export const getResponsiveFontSize = (baseSize: number, isMobile: boolean): number => {
  return isMobile ? getMobileFontSize(baseSize) : baseSize;
};

// =============================================================================
// HERO STYLES
// =============================================================================

export const heroStyles = {
  minHeight: {
    mobile: 300,
    desktop: 500,
  },
  title: {
    mobile: { fontSize: "2rem", fontWeight: 700 },
    desktop: { fontSize: "3rem", fontWeight: 700 },
  },
  subtitle: {
    mobile: { fontSize: "1rem" },
    desktop: { fontSize: "1.25rem" },
  },
  padding: {
    mobile: { px: 16, py: 40 },
    desktop: { px: 24, py: 80 },
  },
  button: {
    mobile: { px: 20, py: 8 },
    desktop: { px: 32, py: 12 },
  },
} as const;

export const getHeroMinHeight = (baseHeight: number, isMobile: boolean): number => {
  if (isMobile) {
    return Math.max(heroStyles.minHeight.mobile, baseHeight * 0.65);
  }
  return baseHeight || heroStyles.minHeight.desktop;
};

// =============================================================================
// TESTIMONIAL STYLES
// =============================================================================

export const testimonialStyles = {
  padding: {
    mobile: 16,
    desktop: 24,
  },
  quote: {
    mobile: { fontSize: "0.95rem", marginBottom: 16 },
    desktop: { fontSize: "1.125rem", marginBottom: 24 },
  },
  quoteMark: {
    mobile: { fontSize: "2.5rem", top: -6, left: -4 },
    desktop: { fontSize: "3.75rem", top: -8, left: -8 },
  },
  author: {
    mobile: { gap: 12, imageSize: 40, fontSize: "0.9rem", titleFontSize: "0.75rem" },
    desktop: { gap: 16, imageSize: 48, fontSize: "1rem", titleFontSize: "0.875rem" },
  },
  stars: {
    mobile: { size: 16 },
    desktop: { size: 20 },
  },
} as const;

export const getTestimonialStyles = (isMobile: boolean) => {
  return {
    padding: isMobile ? testimonialStyles.padding.mobile : testimonialStyles.padding.desktop,
    quote: isMobile ? testimonialStyles.quote.mobile : testimonialStyles.quote.desktop,
    quoteMark: isMobile ? testimonialStyles.quoteMark.mobile : testimonialStyles.quoteMark.desktop,
    author: isMobile ? testimonialStyles.author.mobile : testimonialStyles.author.desktop,
    stars: isMobile ? testimonialStyles.stars.mobile : testimonialStyles.stars.desktop,
  };
};

// =============================================================================
// CONTAINER STYLES
// =============================================================================

export const containerMaxWidths = {
  full: "100%",
  "screen-xl": "1280px",
  "screen-lg": "1024px",
  "screen-md": "768px",
  "screen-sm": "640px",
  "screen-2xl": "1536px",
} as const;

export type ContainerMaxWidth = keyof typeof containerMaxWidths;

export const justifyContentMap = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
  around: "space-around",
} as const;

export const alignItemsMap = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
  baseline: "baseline",
} as const;

// =============================================================================
// NAVBAR STYLES
// =============================================================================

export const navbarStyles = {
  height: {
    mobile: 56,
    desktop: 64,
  },
  padding: {
    mobile: { px: 16, py: 12 },
    desktop: { px: 24, py: 16 },
  },
  logo: {
    mobile: { height: 32 },
    desktop: { height: 40 },
  },
  link: {
    mobile: { padding: "16px 24px", fontSize: "1rem" },
    desktop: { padding: "8px 16px", fontSize: "0.875rem" },
  },
  cta: {
    mobile: { padding: "12px 24px" },
    desktop: { padding: "10px 20px" },
  },
} as const;

export const getNavbarStyles = (isMobile: boolean) => ({
  padding: isMobile ? navbarStyles.padding.mobile : navbarStyles.padding.desktop,
  logo: isMobile ? navbarStyles.logo.mobile : navbarStyles.logo.desktop,
  link: isMobile ? navbarStyles.link.mobile : navbarStyles.link.desktop,
  cta: isMobile ? navbarStyles.cta.mobile : navbarStyles.cta.desktop,
});

// =============================================================================
// FOOTER STYLES
// =============================================================================

export const footerStyles = {
  padding: {
    mobile: { px: 16, py: 32 },
    desktop: { px: 24, py: 48 },
  },
  grid: {
    mobile: "1fr",
    tablet: "repeat(2, 1fr)",
    desktop: "2fr 1fr 1fr 1fr",
  },
  fontSize: {
    clinicName: { mobile: "1.125rem", desktop: "1.25rem" },
    columnTitle: { mobile: "0.875rem", desktop: "1rem" },
    text: { mobile: "0.8125rem", desktop: "0.875rem" },
  },
} as const;

// =============================================================================
// SERVICE CARD STYLES
// =============================================================================

export const serviceCardStyles = {
  imageHeight: {
    mobile: 140,
    tablet: 160,
    desktop: 192,
  },
  padding: {
    mobile: 16,
    desktop: 24,
  },
  title: {
    mobile: { fontSize: "1rem" },
    desktop: { fontSize: "1.25rem" },
  },
  description: {
    mobile: { fontSize: "0.875rem" },
    desktop: { fontSize: "1rem" },
  },
} as const;

export const getServiceCardStyles = (isMobile: boolean) => ({
  imageHeight: isMobile ? serviceCardStyles.imageHeight.mobile : serviceCardStyles.imageHeight.desktop,
  padding: isMobile ? serviceCardStyles.padding.mobile : serviceCardStyles.padding.desktop,
  title: isMobile ? serviceCardStyles.title.mobile : serviceCardStyles.title.desktop,
  description: isMobile ? serviceCardStyles.description.mobile : serviceCardStyles.description.desktop,
});

// =============================================================================
// DOCTOR CARD STYLES
// =============================================================================

export const doctorCardStyles = {
  imageHeight: {
    mobile: 200,
    desktop: 250,
  },
  padding: {
    mobile: 16,
    desktop: 24,
  },
  name: {
    mobile: { fontSize: "1rem" },
    desktop: { fontSize: "1.25rem" },
  },
} as const;

// =============================================================================
// GALLERY STYLES
// =============================================================================

export const galleryStyles = {
  columns: {
    mobile: 2,
    tablet: 3,
    desktop: 3, // or use prop value
  },
  gap: {
    mobile: 8,
    desktop: 16,
  },
} as const;

// =============================================================================
// FAQ STYLES
// =============================================================================

export const faqStyles = {
  padding: {
    mobile: 16,
    desktop: 24,
  },
  question: {
    mobile: { fontSize: "0.9375rem", padding: "16px 0" },
    desktop: { fontSize: "1rem", padding: "20px 0" },
  },
  answer: {
    mobile: { fontSize: "0.8125rem" },
    desktop: { fontSize: "0.875rem" },
  },
} as const;

// =============================================================================
// PROMOTION BANNER STYLES
// =============================================================================

export const promotionBannerStyles = {
  padding: {
    mobile: { px: 16, py: 24 },
    desktop: { px: 32, py: 32 },
  },
  title: {
    mobile: { fontSize: "1.25rem" },
    desktop: { fontSize: "1.75rem" },
  },
  discount: {
    mobile: { fontSize: "2.25rem" },
    desktop: { fontSize: "3rem" },
  },
  subtitle: {
    mobile: { fontSize: "0.875rem" },
    desktop: { fontSize: "1rem" },
  },
} as const;

// =============================================================================
// CONTACT INFO STYLES
// =============================================================================

export const contactInfoStyles = {
  padding: {
    mobile: { px: 16, py: 24 },
    desktop: { px: 32, py: 32 },
  },
  title: {
    mobile: { fontSize: "1.25rem" },
    desktop: { fontSize: "1.5rem" },
  },
  text: {
    mobile: { fontSize: "0.875rem" },
    desktop: { fontSize: "1rem" },
  },
} as const;

// =============================================================================
// SLIDER STYLES (HeroSlider)
// =============================================================================

export const sliderStyles = {
  arrow: {
    mobile: { size: 36, iconSize: 18, position: 8 },
    desktop: { size: 48, iconSize: 24, position: 16 },
  },
  dot: {
    mobile: {
      activeWidth: 24,
      inactiveWidth: 8,
      height: 8,
      borderRadius: 4,
      gap: 6,
      bottom: 16,
    },
    desktop: {
      activeWidth: 32,
      inactiveWidth: 12,
      height: 12,
      borderRadius: 6,
      gap: 8,
      bottom: 24,
    },
  },
  title: {
    mobile: { fontSize: "2.25rem" },
    desktop: { fontSize: "3.75rem" },
  },
  subtitle: {
    mobile: { fontSize: "1.125rem" },
    desktop: { fontSize: "1.5rem" },
  },
  button: {
    mobile: { px: 20, py: 8, fontSize: "0.875rem" },
    desktop: { px: 32, py: 12, fontSize: "1rem" },
  },
  overlay: {
    opacity: 0.4,
    color: "rgba(0, 0, 0, 0.4)",
  },
  arrowBackground: {
    default: "rgba(255, 255, 255, 0.2)",
    hover: "rgba(255, 255, 255, 0.4)",
  },
  dotBackground: {
    active: "#ffffff",
    inactive: "rgba(255, 255, 255, 0.5)",
  },
} as const;

export const getSliderStyles = (isMobile: boolean) => ({
  arrow: isMobile ? sliderStyles.arrow.mobile : sliderStyles.arrow.desktop,
  dot: isMobile ? sliderStyles.dot.mobile : sliderStyles.dot.desktop,
  title: isMobile ? sliderStyles.title.mobile : sliderStyles.title.desktop,
  subtitle: isMobile ? sliderStyles.subtitle.mobile : sliderStyles.subtitle.desktop,
  button: isMobile ? sliderStyles.button.mobile : sliderStyles.button.desktop,
});

// =============================================================================
// FLOATING CTA STYLES
// =============================================================================

export const floatingCtaStyles = {
  position: {
    mobile: { bottom: 16, right: 16 },
    desktop: { bottom: 24, right: 24 },
  },
  padding: {
    mobile: { px: 16, py: 12 },
    desktop: { px: 24, py: 16 },
  },
  showText: {
    mobile: false,
    desktop: true,
  },
} as const;

// =============================================================================
// BEFORE/AFTER STYLES
// =============================================================================

export const beforeAfterStyles = {
  height: {
    mobile: 240,
    desktop: 400,
  },
  minWidth: 200,
  defaultImages: {
    before: "https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=600",
    after: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600",
  },
  slider: {
    lineWidth: 4,
    handleSize: 40,
  },
  label: {
    padding: { px: 12, py: 4 },
    fontSize: "0.875rem",
    borderRadius: 12,
    top: 16,
    horizontalOffset: 16,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
} as const;

export const getBeforeAfterStyles = (isMobile: boolean) => ({
  height: isMobile ? beforeAfterStyles.height.mobile : beforeAfterStyles.height.desktop,
});

// =============================================================================
// CONTACT FORM STYLES
// =============================================================================

export const contactFormStyles = {
  padding: {
    mobile: 16,
    desktop: 32,
  },
  input: {
    mobile: { padding: "10px 12px", fontSize: "0.875rem" },
    desktop: { padding: "12px 16px", fontSize: "1rem" },
  },
  button: {
    mobile: { padding: "10px 16px", fontSize: "0.875rem" },
    desktop: { padding: "12px 24px", fontSize: "1rem" },
  },
} as const;

// =============================================================================
// VIDEO STYLES
// =============================================================================

export const videoStyles = {
  aspectRatios: {
    "16:9": "56.25%",
    "4:3": "75%",
    "1:1": "100%",
  },
} as const;

// =============================================================================
// SHADOWS
// =============================================================================

export const shadows = {
  none: "none",
  sm: "0 1px 2px rgba(0,0,0,0.05)",
  md: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
  lg: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
  xl: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
  card: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
} as const;

// =============================================================================
// COLORS
// =============================================================================

export const defaultColors = {
  primary: "#3b82f6",
  secondary: "#10b981",
  text: {
    primary: "#1f2937",
    secondary: "#4b5563",
    muted: "#6b7280",
    light: "#9ca3af",
  },
  background: {
    white: "#ffffff",
    light: "#f9fafb",
    dark: "#1f2937",
  },
  border: "#e5e7eb",
} as const;

// =============================================================================
// TRANSITIONS
// =============================================================================

export const transitions = {
  fast: "0.15s ease",
  normal: "0.2s ease",
  slow: "0.3s ease",
} as const;

// =============================================================================
// EXPORT CSS MEDIA QUERIES HELPER
// =============================================================================

/**
 * Generate CSS media query string for mobile
 * Use (max-width: 767px) to match breakpoints.mobile - 1
 */
export const getMobileMediaQuery = (): string => `@media (max-width: ${breakpoints.mobile - 1}px)`;

/**
 * Generate CSS media query string for tablet
 */
export const getTabletMediaQuery = (): string =>
  `@media (min-width: ${breakpoints.mobile}px) and (max-width: ${breakpoints.tablet - 1}px)`;

/**
 * Generate CSS media query string for desktop
 */
export const getDesktopMediaQuery = (): string => `@media (min-width: ${breakpoints.desktop}px)`;

/**
 * Generate CSS container query string for mobile
 * Used in preview mode
 */
export const getMobileContainerQuery = (): string => `@container (max-width: ${breakpoints.mobile - 1}px)`;
